/* eslint @typescript-eslint/explicit-function-return-type: 1, @typescript-eslint/no-explicit-any: 1 -- TODO fix types */
import { AsyncPipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { combineLatest, exhaustMap, filter, fromEvent, map, Observable, take, timer } from 'rxjs';

import { UserStoreSelectors } from '@hosty-app/app-store';
import { ERole } from '@hosty-app/types';

import { PermissionsService, SupportChatService } from '@hosty-web/services';

import { AppStoreActions, AppStoreSelectors } from '../../core/store/app';

import { MobileBottomMenuComponent } from './mobile-bottom-menu/mobile-bottom-menu.component';
import { MobileHeaderComponent } from './mobile-header/mobile-header.component';
import { SidebarComponent } from './sidebar/sidebar.component';
import { isItemAllowed, SIDEBAR_ITEMS, SidebarItem } from './sidebar-items';

// @ts-ignore
const appMetadata = require('../../../assets/version.json');

@UntilDestroy()
@Component({
  selector: 'hosty-app-shell-sidebar',
  templateUrl: './shell-sidebar.component.html',
  styleUrls: ['./shell-sidebar.component.scss'],
  imports: [
    SidebarComponent,
    RouterOutlet,
    MobileBottomMenuComponent,
    MobileHeaderComponent,
    AsyncPipe,
  ],
})
export class ShellSidebarComponent implements OnInit {
  sidebarVisible$: Observable<boolean>;
  mobileLayout$: Observable<boolean>;
  sidebarItems$: Observable<SidebarItem[]>;
  bottomMenuItems$: Observable<SidebarItem[]>;
  bottomMenuHidden = false;
  keyboardShown = false;
  forceRefresh = false;
  bottomMenuMaxItemsCount = 5;

  constructor(
    private store$: Store,
    public readonly supportChat: SupportChatService,
    private http: HttpClient,
    private permissions: PermissionsService,
  ) {
    if (window['cordova']) {
      fromEvent(window, 'keyboardHeightWillChange')
        .pipe(untilDestroyed(this))
        .subscribe((event) => {
          console.log(event);
          this.keyboardShown = event['keyboardHeight'] > 0;
        });
    } else {
      timer(0, 60000)
        .pipe(
          exhaustMap(() => this.http.get<{ version: string }>('/assets/version.json')),
          filter(({ version }) => version !== appMetadata.version),
          take(1),
          untilDestroyed(this),
        )
        .subscribe(() => {
          this.forceRefresh = true;
        });
    }
  }

  ngOnInit(): void {
    this.mobileLayout$ = this.store$.select(AppStoreSelectors.selectIsMobile);
    this.sidebarVisible$ = this.store$.select(AppStoreSelectors.selectSidebarVisibleState);

    this.bottomMenuItems$ = combineLatest([
      this.store$.select(UserStoreSelectors.selectUserProfile),
      this.mobileLayout$,
    ]).pipe(
      filter(([profile]) => !!profile),
      map(([profile, isMobile]) => {
        if (!isMobile) return [];
        this.bottomMenuMaxItemsCount = profile.role === ERole.ROLE_SITE_MAIN_CLEANER ? 6 : 5;
        const hasPermission = (e: SidebarItem): boolean =>
          isItemAllowed(e, (p) => this.permissions.checkAccessByPermission(p));
        const filterFn = (e: SidebarItem) => {
          return e.bottomMenuOrderByRole?.includes(profile.role) && hasPermission(e);
        };
        const defaultItems =
          profile.menuItems
            // A saved menuItems id that no longer matches a sidebar item used to
            // leave `undefined` in the list, which threw while rendering the tab
            // bar. Permissions were never applied here either, so the menu could
            // offer tabs the guard immediately bounced.
            ?.map((m) => SIDEBAR_ITEMS.find((s) => s.id === m))
            .filter((item): item is SidebarItem => !!item)
            .filter(hasPermission) ?? SIDEBAR_ITEMS.filter(filterFn);
        return [
          ...(profile.role === ERole.ROLE_SITE_CUSTOM ? defaultItems.slice(0, 4) : defaultItems),
          ...(profile.role !== ERole.ROLE_SITE_CUSTOM || defaultItems.length > 4
            ? [
                {
                  title: 'More',
                  icon: 'hosty-icon-burger',
                  id: 'more-button',
                  bottomMenuOrderByRole: [
                    ERole.ROLE_SITE_SUPER_ADMIN,
                    ERole.ROLE_SITE_ADMIN,
                    ERole.ROLE_SITE_MANAGER,
                    ERole.ROLE_SITE_CUSTOM,
                  ],
                  handler: () => this.toggleSidebar(),
                } as SidebarItem,
              ]
            : []
          ).filter(filterFn),
        ];
      }),
    );
    this.sidebarItems$ = combineLatest([this.bottomMenuItems$, this.mobileLayout$]).pipe(
      map(([bottomMenuItems, isMobile]) => {
        let items: SidebarItem[] = SIDEBAR_ITEMS;
        if (isMobile) {
          items = items.filter((e) => !bottomMenuItems.some((item) => item.route === e.route));
        }
        return items.map((item) => {
          if (item.id === 'spt-chat') {
            return { ...item, handler: () => this.openSupportChat() };
          }

          return item;
        });
      }),
    );
  }

  toggleSidebar() {
    this.store$.dispatch(AppStoreActions.toggleSidebar());
  }

  showBottomMenu() {
    this.bottomMenuHidden = false;
  }

  hideBottomMenu() {
    this.bottomMenuHidden = true;
  }

  openSupportChat() {
    this.supportChat.open();
  }
}
